import { useCallback, useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Calendar as CalendarIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import {
  ODataGrid,
  type ODataGridConfig,
  type GridToolbarHelpers,
} from '@inuxe/prime-odata-grid';
import { useStore } from '../../../hooks/useStore';
import type { IAppointment } from '../../../models/appointment/AppointmentModel';
import { buildLegacyGridParams, buildODataMappingFromColumns } from '../../../utils/odataGridUtils';
import { getPastAppointmentRowClassName } from '../../../utils/gridRowClassNames';
import gridStyles from '../../../styles/ODataGridPage.module.css';
import { useAppointmentsColumns } from './Appointments.grid';
import styles from './Appointments.module.css';
import NewAppointmentPopup from './parts/NewAppointmentPopup';
import AppointmentForHospitalTagPopup from './parts/AppointmentForHospitalTagPopup';
import EditAppointmentPopup from './parts/EditAppointmentPopup';
import AppointmentDetailPopup from './parts/AppointmentDetailPopup';
import TodayRoutePopup from './parts/TodayRoutePopup';
import DeleteAppointmentDialog from './parts/DeleteAppointmentDialog';
import AppointmentCalendar from './parts/AppointmentCalendar';
import AppointmentsToolbar from './parts/AppointmentsToolbar';

const GRID_ID = 'appointments-grid';

const Appointments = () => {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [calendarKey, setCalendarKey] = useState(0);
  const { popupStore, appointmentStore } = useStore();
  const columns = useAppointmentsColumns();

  const oDataMapping = useMemo(
    () => buildODataMappingFromColumns(columns),
    [columns]
  );

  const gridConfig = useMemo<ODataGridConfig<IAppointment>>(
    () => ({
      gridId: GRID_ID,
      rows: 20,
      oDataMapping,
      fetchRows: async params => {
        const result = await appointmentStore.listAppointments(
          buildLegacyGridParams(params, { oDataMapping })
        );
        return { rows: result.rows, totalRecords: result.lastRow };
      },
    }),
    [appointmentStore, oDataMapping]
  );

  const onGridReady = useCallback(
    (helpers: GridToolbarHelpers<IAppointment>) => {
      appointmentStore.setGridHelpers(helpers);
    },
    [appointmentStore]
  );

  const getRowClassName = useCallback(
    (row: IAppointment) => getPastAppointmentRowClassName(row.appointmentDate),
    []
  );

  const exportToExcel = useCallback(
    async (helpers: GridToolbarHelpers<IAppointment>) => {
      try {
        const queryState = helpers.getQueryState({ skip: 0, top: 1000000 });
        const result = await appointmentStore.listAppointments(buildLegacyGridParams(queryState));

        if (!result?.rows.length) {
          toast.warning("Excel'e aktarılacak veri bulunamadı");
          return;
        }

        const exportData = result.rows.map(item => ({
          ID: item.id || '',
          'Kişi Adı': item.personName || '',
          'Etiket Numarası': item.requestNumber || '',
          Hastane: item.hospitalName || '',
          Kullanıcı: item.userName || item.userId || '',
          'Randevu Tarihi': item.appointmentDate
            ? new Date(item.appointmentDate).toLocaleDateString('tr-TR')
            : '',
          'Başlangıç Saati': item.appointmentStartTime || '',
          'Bitiş Saati': item.appointmentEndTime || '',
          'Cihaz Listesi': item.deviceList || '',
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Randevular');
        XLSX.writeFile(wb, `Randevular_${new Date().toISOString().split('T')[0]}.xlsx`);

        toast.success(`${exportData.length} randevu Excel dosyasına aktarıldı`);
      } catch (error) {
        console.error('Excel export error:', error);
        toast.error("Excel'e aktarılırken bir hata oluştu");
      }
    },
    [appointmentStore]
  );

  const refreshCalendar = useCallback(() => {
    setCalendarKey(prev => prev + 1);
  }, []);

  const toggleViewMode = () => {
    setViewMode(prev => {
      const next = prev === 'list' ? 'calendar' : 'list';
      if (next === 'calendar') {
        refreshCalendar();
      }
      return next;
    });
  };

  useEffect(() => {
    if (
      viewMode === 'calendar' &&
      !popupStore.isNewAppointmentOpen &&
      !popupStore.isAppointmentForHospitalTagOpen &&
      !popupStore.isEditAppointmentOpen &&
      !popupStore.isTodayRoutePopupOpen
    ) {
      setCalendarKey(prev => prev + 1);
    }
  }, [
    popupStore.isNewAppointmentOpen,
    popupStore.isAppointmentForHospitalTagOpen,
    popupStore.isEditAppointmentOpen,
    popupStore.isTodayRoutePopupOpen,
    viewMode,
  ]);

  const handleDeleteConfirmedRefresh = useCallback(() => {
    if (viewMode === 'calendar') {
      refreshCalendar();
    }
  }, [viewMode, refreshCalendar]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerInner}>
            <div className={styles.titleRow}>
              <span className={styles.titleIcon} aria-hidden="true">
                <CalendarIcon size={20} strokeWidth={2} />
              </span>
              <div className={styles.titleContent}>
                <h2 className={styles.mainTitle}>Randevular</h2>
                <p className={styles.pageDescription}>
                  Randevuları liste veya takvim görünümünde yönetin.
                </p>
              </div>
            </div>
            <span className={styles.viewBadge}>
              {viewMode === 'list' ? 'Liste' : 'Takvim'}
            </span>
          </div>
        </div>

        {viewMode === 'list' ? (
          <div className={styles.gridContainer}>
            <ODataGrid<IAppointment>
              gridId={GRID_ID}
              config={gridConfig}
              columns={columns}
              dataKey="id"
              wrapperClassName={gridStyles.oDataGridPage}
              resizableColumns
              onGridReady={onGridReady}
              rowClassName={getRowClassName}
              toolbarLeft={helpers => (
                <AppointmentsToolbar
                  viewMode="list"
                  onNewAppointment={() => popupStore.setIsNewAppointmentOpen(true)}
                  onAppointmentForHospitalTag={() =>
                    popupStore.setIsAppointmentForHospitalTagOpen(true)
                  }
                  onTodayRoute={() => popupStore.setIsTodayRoutePopupOpen(true)}
                  onToggleView={toggleViewMode}
                  onExportExcel={exportToExcel}
                  onRefreshList={h => h.refreshGrid()}
                  gridHelpers={helpers}
                />
              )}
            />
          </div>
        ) : (
          <div className={styles.calendarWrapper}>
            <AppointmentsToolbar
              viewMode="calendar"
              onNewAppointment={() => popupStore.setIsNewAppointmentOpen(true)}
              onAppointmentForHospitalTag={() =>
                popupStore.setIsAppointmentForHospitalTagOpen(true)
              }
              onTodayRoute={() => popupStore.setIsTodayRoutePopupOpen(true)}
              onToggleView={toggleViewMode}
              onRefreshCalendar={refreshCalendar}
            />
            <AppointmentCalendar key={calendarKey} />
          </div>
        )}
      </div>

      {popupStore.isNewAppointmentOpen && <NewAppointmentPopup />}
      {popupStore.isAppointmentForHospitalTagOpen && <AppointmentForHospitalTagPopup />}
      {popupStore.isEditAppointmentOpen && <EditAppointmentPopup />}
      {popupStore.isAppointmentDetailOpen && <AppointmentDetailPopup />}
      {popupStore.isTodayRoutePopupOpen && <TodayRoutePopup />}
      <DeleteAppointmentDialog onDeleted={handleDeleteConfirmedRefresh} />
    </>
  );
};

export default observer(Appointments);
